// src/pages/OrderList.js
import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  Drawer,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
  Divider,
  LinearProgress,
  useMediaQuery
} from '@mui/material';
import { DataGrid, GridFooterContainer, GridPagination, useGridApiRef } from '@mui/x-data-grid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import "dayjs/locale/th";
import Swal from 'sweetalert2';
import api from '../adapters/api';
import { CartContext } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";
import { usePreviewPDF, useShareQuotationImage, preloadQuotationImage } from "../hooks/usePreviewPDF";
import QuotationDocument from '../components/formDocument/QuotationDocument';
import yearAdapter from "../adapters/yearAdapter";
import DocTrail, { useDocTrail, DocCompareButton, CopyDocNoButton, DeliveryPlace } from '../components/DocTrail';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useInlineOrders } from '../components/insights/useInsightInline';

dayjs.extend(relativeTime);
dayjs.locale('th');

function friendlyTime(value) {
  if (!value) return '';
  const d = dayjs(value);
  const diffMin = dayjs().diff(d, 'minute');
  if (diffMin < 60) return d.fromNow();
  if (d.isSame(dayjs(), 'day')) return `วันนี้ ${d.format('HH:mm')}`;
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return `เมื่อวาน ${d.format('HH:mm')}`;
  return d.format('DD/MM/BBBB HH:mm');
}

function EmptyState({ searching }) {
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography color="text.secondary">
        {searching ? 'กำลังค้นหาเพิ่มเติม...' : 'ไม่พบรายการที่ตรงกับตัวกรอง'}
      </Typography>
    </Box>
  );
}

// ตัดวงเล็บ/เบอร์โทรท้ายชื่อเซลล์ออก ให้เหลือแต่ชื่อ
const cleanSaleName = (value) =>
  (value || '').replace(/\s*[\(\（][^)）]*[\)\）]/g, '').replace(/\s+[0-9][\d\s\-]{7,}/g, '').trim();

const fmt2 = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function FooterStat({ label, value, unit, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' }, color: color || 'text.primary' }}>
        {value}
      </Typography>
      {unit && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
          {unit}
        </Typography>
      )}
    </Box>
  );
}

function TableFooter({ selectionModel = [], rowsById = {} }) {
  const selectedRows = selectionModel.map(id => rowsById[id]).filter(Boolean);
  const count        = selectedRows.length;
  const totalAmount  = selectedRows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0);
  const totalWeight  = selectedRows.reduce((s, r) => s + (parseFloat(r.total_weight) || 0), 0);

  return (
    <GridFooterContainer
      sx={{
        px: { xs: 1, sm: 2 },
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1 },
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        minHeight: 52,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          flex: 1,
          minWidth: 0,
          flexWrap: 'wrap',
          py: 0.5,
        }}
      >
        {count === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
            เลือกแถวเพื่อดูยอดรวม
          </Typography>
        ) : (
          <>
            <FooterStat label="เลือก" value={count.toLocaleString('th-TH')} unit="รายการ" color="primary.main" />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
            <FooterStat label="จำนวนเงิน" value={`฿${fmt2(totalAmount)}`} color="success.dark" />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
            <FooterStat label="น้ำหนัก" value={fmt2(totalWeight)} unit="กก." />
          </>
        )}
      </Box>
      <GridPagination
        sx={{
          flexShrink: 0,
          '& .MuiTablePagination-toolbar': {
            alignItems: 'center',
            minHeight: 52,
            pl: { xs: 0, sm: 2 },
          },
          '& .MuiTablePagination-selectLabel': {
            display: { xs: 'none', sm: 'block' },
            m: 0,
          },
          '& .MuiTablePagination-input': {
            display: { xs: 'none', sm: 'flex' },
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            m: 0,
          },
        }}
      />
    </GridFooterContainer>
  );
}

const DRAFT_PRIORITY_CONFIG = {
  urgent:  { emoji: '🔴', label: 'ด่วนมาก', color: '#b71c1c', bg: '#ffebee' },
  high:    { emoji: '🟠', label: 'ด่วน',    color: '#e65100', bg: '#fff3e0' },
  today:   { emoji: '🟡', label: 'วันนี้',   color: '#f57f17', bg: '#fffde7' },
  waiting: { emoji: '🔵', label: 'รอ',       color: '#1565c0', bg: '#e3f2fd' },
};

// Tab look — colors match the status chips in the detail drawer
const TAB_META = {
  DRAFT: { icon: EditNoteOutlinedIcon,     color: '#6a1b9a', bg: '#f3e5f5' },
  QT:    { icon: RequestQuoteOutlinedIcon, color: '#1565c0', bg: '#e3f2fd' },
  SO:    { icon: AssignmentOutlinedIcon,   color: '#2e7d32', bg: '#e8f5e9' },
  SL:    { icon: ReceiptLongOutlinedIcon,  color: '#c62828', bg: '#fce4ec' },
};

// Must match ORDER_LIMIT_PER_STATUS in backend/routes/orders.js — a full tab shows "1,000+"
const ORDER_LIMIT_PER_STATUS = 1000;

export default function OrderList() {
  const previewPDF = usePreviewPDF();
  const shareQuotationImage = useShareQuotationImage();
  // ปุ่ม PDF → เลือกเปิดเป็นไฟล์ PDF หรือส่งเป็นรูปภาพ  { anchorEl, docNo }
  const [exportMenu, setExportMenu] = useState(null);

  const TABS = ['DRAFT', 'QT', 'SO', 'SL'];
  const TAB_LABELS = {
    DRAFT: "Draft",
    QT: "ใบเสนอราคา",
    SO: "ใบสั่งขาย",
    SL: "ขายสินค้า",
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    return searchParams.get("tab") || "QT";
  });
  const [data, setData] = useState({ QT: [], SO: [], SL: [] });
  const [loading, setLoading] = useState(false);       // full overlay — first load / user actions
  const [refreshing, setRefreshing] = useState(false); // background reload — thin bar, grid stays usable

  // filters
  const [filterText, setFilterText] = useState(() => searchParams.get('search') || '');
  const [dateRange, setDateRange] = useState(() => {
    const from = searchParams.get('from');
    const to   = searchParams.get('to');
    return [
      from ? dayjs(from) : null,
      to   ? dayjs(to)   : null
    ];
  });
  const [branch, setBranch]           = useState(() => searchParams.get('branch')    || '');
  const [salesperson, setSalesperson] = useState(() => searchParams.get('sales')     || '');
  const [department, setDepartment]   = useState(() => searchParams.get('dept')      || '');

  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  // dropdown options
  const [branchList, setBranchList]       = useState([]);
  const [salespersons, setSalespersons]   = useState([]);
  const [departmentList, setDepartmentList] = useState([]);

  const [drawerOrder, setDrawerOrder] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  // มือถือ + แท็บเล็ต: ตารางหลายคอลัมน์บีบจนอ่านไม่ออก → แสดงบิลเป็นการ์ดแถวเดียวแทน
  const isCompact = useMediaQuery(theme => theme.breakpoints.down('md'));
  // insight ฝังในหน้าจอ: สถานะสต็อกของ SO ค้าง (null = ผู้ใช้ไม่ได้เปิดใช้)
  const soBadges = useInlineOrders();
  const navigate = useNavigate();

  // ――― Cart Context ―――
  const { cart, loadOrderIntoCart, setCartGuidActive, deleteCart, fetchCartHeader, setLockInfo, subscribeToLockUpdates, lockInfo, resetCartState, customerId, currentOrderNo, addNotificationListener } = useContext(CartContext);
  const { currentUser } = useContext(AuthContext);
  const [editLoading, setEditLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [billDetails, setBillDetails] = useState(null);
  // header from GET /orders/:docNo — the list row has no delivery place
  const [billHeader, setBillHeader] = useState(null);
  const [billDetailsLoading, setBillDetailsLoading] = useState(false);
  const [refDrawerOrder, setRefDrawerOrder] = useState(null);
  const [refBillDetails, setRefBillDetails] = useState(null);
  const [refDrawerLoading, setRefDrawerLoading] = useState(false);
  const refDocSeqRef = useRef(0);

  const handleRefDocOpen = async (docNo) => {
    // moving along the doc trail can fire several opens — only the latest one wins
    const seq = ++refDocSeqRef.current;
    setRefDrawerLoading(true);
    setRefDrawerOrder({ doc_no: docNo });
    setRefBillDetails(null);
    try {
      const { data } = await api.get(`/api/orders/${encodeURIComponent(docNo)}`);
      if (seq !== refDocSeqRef.current) return;
      if (data?.header) {
        setRefDrawerOrder(data.header);
        setRefBillDetails(data.details || []);
      }
    } catch (err) {
      console.error('ref doc fetch error:', err);
      if (seq === refDocSeqRef.current) setRefDrawerOrder(null);
    } finally {
      if (seq === refDocSeqRef.current) setRefDrawerLoading(false);
    }
  };

  const drawerTrail = useDocTrail(
    drawerOrder && ['QT', 'SO', 'SL'].includes(drawerOrder.status) ? drawerOrder.doc_no : null
  );
  const refDrawerTrail = useDocTrail(refDrawerOrder?.doc_no || null);

  const handleRestoreDraft = async () => {
    if (!drawerOrder?.cart_guid || restoreLoading) return;
    setRestoreLoading(true);
    try {
      await api.post(`/api/draft/${drawerOrder.cart_guid}/restore`);
      const restoredNo = drawerOrder.doc_no;
      setDrawerOrder(null);
      await loadAll();
      Swal.fire({
        icon: 'success',
        title: 'กู้คืน Draft สำเร็จ',
        text: `Draft ${restoredNo} ถูกกู้คืนแล้ว`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('restore draft error:', err);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: err.response?.data?.error || 'ไม่สามารถกู้คืน Draft ได้',
        confirmButtonColor: '#1976d2',
        confirmButtonText: 'ตกลง',
      });
    } finally {
      setRestoreLoading(false);
    }
  };

  const prevDraftGuidsRef = useRef(new Set());
  const notifDebounceRef = useRef(null);
  const [newDraftGuids, setNewDraftGuids] = useState(new Set());
  const highlightParam = searchParams.get('highlight');
  const [urlHighlightGuid, setUrlHighlightGuid] = useState(highlightParam);

  const viewDeletedParam = searchParams.get('view_deleted');
  useEffect(() => {
    if (!viewDeletedParam) return;
    api.get(`/api/draft/${viewDeletedParam}`)
      .then(({ data }) => {
        if (data?.header) {
          setDrawerOrder({
            ...data.header,
            doc_no: data.header.ref_doc_no,
            status: 'DELETED',
            details: data.details || [],
          });
        }
      })
      .catch(err => console.error('view_deleted fetch error:', err))
      .finally(() => {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete('view_deleted');
          return next;
        }, { replace: true });
      });
  }, [viewDeletedParam]);

  useEffect(() => {
    if (!drawerOrder || !['QT', 'SO', 'SL', 'DRAFT'].includes(drawerOrder.status)) {
      setBillDetails(null);
      setBillHeader(null);
      return;
    }
    setBillDetailsLoading(true);
    setBillHeader(null);
    const fetchDetails = drawerOrder.status === 'DRAFT'
      ? api.get(`/api/draft/${drawerOrder.cart_guid}`).then(({ data }) => ({ details: data?.details || [] }))
      : api.get(`/api/orders/${encodeURIComponent(drawerOrder.doc_no)}`).then(({ data }) => ({ details: data?.details || [], header: data?.header || null }));
    fetchDetails
      .then(({ details, header }) => { setBillDetails(details); setBillHeader(header || null); })
      .catch(() => setBillDetails([]))
      .finally(() => setBillDetailsLoading(false));
  }, [drawerOrder?.doc_no, drawerOrder?.cart_guid, drawerOrder?.status]);

  const lockInfoRef = useRef(null);
  useEffect(() => { lockInfoRef.current = lockInfo; }, [lockInfo]);

  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      ['branch', 'sales', 'dept', 'from', 'to', 'tab'].forEach(k => next.delete(k));
      if (branch)       next.set('branch', branch);
      if (salesperson)  next.set('sales',  salesperson);
      if (department)   next.set('dept',   department);
      if (dateRange[0]) next.set('from',   dateRange[0].format("YYYY-MM-DD"));
      if (dateRange[1]) next.set('to',     dateRange[1].format("YYYY-MM-DD"));
      next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [dateRange, branch, salesperson, department, tab]);

  // Filters sent to the server — the API only returns the newest bills per tab,
  // so search/date/branch/sales must be applied there for older bills to be found
  const [debouncedSearch, setDebouncedSearch] = useState(() => filterText.trim());
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filterText.trim()), 400);
    return () => clearTimeout(timer);
  }, [filterText]);
  const serverFilters = useMemo(() => {
    const f = {};
    if (debouncedSearch) f.search = debouncedSearch;
    if (dateRange[0]?.isValid()) f.startDate = dateRange[0].format('YYYY-MM-DD');
    if (dateRange[1]?.isValid()) f.endDate = dateRange[1].format('YYYY-MM-DD');
    if (branch) f.branch = branch;
    if (salesperson) f.sale = salesperson;
    return f;
  }, [debouncedSearch, dateRange, branch, salesperson]);
  // Searching a bill number that isn't in the table (e.g. a QT already pulled into an SO/SL,
  // which the list hides) — look up its doc trail so the user can see where it ended up
  const searchDocNo = useMemo(() => {
    const s = debouncedSearch.toUpperCase();
    if (!/^[A-Z0-9][A-Z0-9-]{5,}$/.test(s) || !/\d{4}/.test(s)) return null;
    const inTable = ['QT', 'SO', 'SL'].some(k => (data[k] || []).some(r => (r.doc_no || '').toUpperCase() === s));
    return inTable ? null : s;
  }, [debouncedSearch, data]);
  const searchTrail = useDocTrail(searchDocNo);
  const searchTrailDoc = searchDocNo && searchTrail ? searchTrail.find(r => r.doc_no === searchDocNo) : null;
  const serverFiltersRef = useRef(serverFilters);
  serverFiltersRef.current = serverFilters;
  const serverFiltersKey = JSON.stringify(serverFilters);
  // Orders and drafts are versioned separately so an orders-only reload doesn't discard drafts from a full one
  const ordersSeqRef = useRef(0);
  const draftsSeqRef = useRef(0);
  const pendingLoadsRef = useRef({ fg: 0, bg: 0 });

  // load orders
  // opts.background — show the thin progress bar instead of the grid overlay
  // opts.ordersOnly — refetch QT/SO/SL only (drafts aren't filtered server-side)
  const loadAll = useCallback(async (opts = {}) => {
    const withDrafts = !opts.ordersOnly;
    const ordersSeq = ++ordersSeqRef.current;
    const draftsSeq = withDrafts ? ++draftsSeqRef.current : null;
    const kind = opts.background ? 'bg' : 'fg';
    const setBusy = opts.background ? setRefreshing : setLoading;
    pendingLoadsRef.current[kind] += 1;
    setBusy(true);
    const prevGuids = prevDraftGuidsRef.current;
    try {
      const [ordersRes, draftsRes] = await Promise.all([
        api.post('/api/orders', serverFiltersRef.current),
        withDrafts ? api.get('/api/orders/draft') : null,
      ]);
      // a newer load (e.g. filter changed while this one was in flight) supersedes this one
      const ordersFresh = ordersSeq === ordersSeqRef.current;
      const draftsFresh = withDrafts && draftsSeq === draftsSeqRef.current;
      if (!ordersFresh && !draftsFresh) return;

      const orders = ordersRes.data || [];
      const drafts = draftsFresh && Array.isArray(draftsRes.data) ? draftsRes.data : [];

      const grouped = { QT: [], SO: [], SL: [], DRAFT: [] };
      orders.forEach(r => {
        grouped[r.status]?.push({
          ...r,
          doc_date: r.doc_date ? new Date(r.doc_date) : null,
          total_amount: parseFloat(r.total_amount) || 0,
        });
      });

      drafts.forEach(d => {
        grouped.DRAFT.push({
          ...d,
          doc_date: d.doc_date ? new Date(d.doc_date) : null,
          total_amount: parseFloat(d.total_amount) || 0,
        });
      });

      const currentGuids = new Set(grouped.DRAFT.map(d => d.cart_guid));
      if (draftsFresh) prevDraftGuidsRef.current = currentGuids;

      if (draftsFresh && opts.trackNew) {
        const newGuids = new Set([...currentGuids].filter(g => !prevGuids.has(g)));
        if (newGuids.size > 0) {
          setNewDraftGuids(prev => new Set([...prev, ...newGuids]));
          setTimeout(() => {
            setNewDraftGuids(prev => {
              const next = new Set(prev);
              newGuids.forEach(g => next.delete(g));
              return next;
            });
          }, 8000);
        }
      }

      setData(prev => ({
        ...prev,
        ...(ordersFresh ? { QT: grouped.QT, SO: grouped.SO, SL: grouped.SL } : {}),
        ...(draftsFresh ? { DRAFT: grouped.DRAFT } : {}),
      }));
    } catch (err) {
      console.error(err);
    } finally {
      pendingLoadsRef.current[kind] -= 1;
      if (pendingLoadsRef.current[kind] === 0) setBusy(false);
    }
  }, []);

  // First load shows the overlay; later filter changes reload orders in the background
  const didInitialLoadRef = useRef(false);
  useEffect(() => {
    if (!didInitialLoadRef.current) {
      didInitialLoadRef.current = true;
      loadAll();
      return;
    }
    loadAll({ ordersOnly: true, background: true });
  }, [loadAll, serverFiltersKey]);

  const loadLockStatus = useCallback(async () => {
    try {
      const { data: locks } = await api.get('/api/orders/lock-status');
      const lockMap = {};
      locks.forEach(({ doc_no, guid_code }) => { lockMap[doc_no] = guid_code; });
      setData(prev => {
        const next = {};
        for (const [status, rows] of Object.entries(prev)) {
          // lock-status lists only locked bills, so anything missing from it is unlocked
          next[status] = status === 'DRAFT' ? rows : rows.map(r => ({
            ...r,
            guid_code: lockMap[r.doc_no] || '',
          }));
        }
        return next;
      });
    } catch (err) {
      console.error('loadLockStatus error:', err);
    }
  }, []);

  useEffect(() => {
    return subscribeToLockUpdates(loadLockStatus);
  }, [subscribeToLockUpdates, loadLockStatus]);

  useEffect(() => {
    if (!addNotificationListener) return;
    const unsub = addNotificationListener(() => {
      clearTimeout(notifDebounceRef.current);
      notifDebounceRef.current = setTimeout(() => loadAll({ trackNew: true, background: true }), 300);
    });
    return () => {
      unsub();
      clearTimeout(notifDebounceRef.current);
    };
  }, [addNotificationListener, loadAll]);

  useEffect(() => {
    if (!highlightParam) {
      setUrlHighlightGuid(null);
      return;
    }
    setUrlHighlightGuid(highlightParam);
    setTab(searchParams.get('tab') || 'DRAFT');
    const timer = setTimeout(() => {
      setUrlHighlightGuid(null);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('highlight');
        return next;
      }, { replace: true });
    }, 8000);
    return () => clearTimeout(timer);
  }, [highlightParam]);

  // load branch & salespersons
  useEffect(() => {
    async function loadFilters() {
      try {
        const [{ data: branches }, { data: sales }] = await Promise.all([
          api.post('/api/getBranchList'),
          api.post('/api/getSalespersons')
        ]);
        setBranchList(branches);
        setSalespersons(sales);
        
        setDepartmentList([
          'แผนกขายเครดิต',
          'แผนกขายออนไลน์',
          'ขายหน้าร้านสันทราย',
          'ขายหน้าร้านสันกำแพง'
        ]);
      } catch (err) {
        console.error(err);
      }
    }
    loadFilters();
  }, []);

  // apply filters
  const applyFilters = list => {
    return list.filter(r => {
      // branch
      if (branch && r.branch_code !== branch) return false;
      // salesperson
      if (salesperson && r.sale_code !== salesperson) return false;
      // department via salespersons
      if (department) {
        const user = salespersons.find(u => u.code === r.sale_code);
        if (!user || !user.groups.some(g => g.group_name === department)) return false;
      }
      // text
      const ft = filterText.trim().toLowerCase();
      if (ft) {
        const inDoc    = (r.doc_no        || '').toLowerCase().includes(ft);
        const inCust   = (r.customer_name || '').toLowerCase().includes(ft);
        const inRemark = (r.remark       || '').toLowerCase().includes(ft);
        const inRef    = Array.isArray(r.ref_docs)
          && r.ref_docs.some(d => (d || '').toLowerCase().includes(ft));
      
        if (!(inDoc || inCust || inRemark || inRef)) {
          return false;
        }
      }
      // date
      if (dateRange[0]) {
        // แปลง r.doc_date ให้เป็น dayjs แล้ว startOf('day')
        const rowDay = dayjs(r.doc_date).startOf('day');
        const fromDay = dateRange[0].startOf('day');
        if (rowDay.isBefore(fromDay)) return false;
      }
      if (dateRange[1]) {
        const rowDay = dayjs(r.doc_date).startOf('day');
        const toDay = dateRange[1].startOf('day');
        if (rowDay.isAfter(toDay)) return false;
      }
      return true;
    });
  };

  // filtered counts
  const filteredCounts = useMemo(
    () =>
      TABS.reduce(
        (acc, s) => ({ ...acc, [s]: applyFilters(data[s] || []).length }),
        {}
      ),
    [data, filterText, dateRange, branch, salesperson, department]
  );

  // Pulse a tab's count when it goes up while the user is looking at another tab (e.g. a new
  // draft arrives). Increases right after a filter/search change — including the server reload
  // that follows it — are the user's own doing, so they're ignored.
  const [bumpedTabs, setBumpedTabs] = useState(() => new Set());
  const prevCountsRef = useRef(null);
  const filterChangedAtRef = useRef(Date.now());
  const filterKey = JSON.stringify([filterText, dateRange, branch, salesperson, department]);
  useEffect(() => { filterChangedAtRef.current = Date.now(); }, [filterKey]);
  useEffect(() => {
    const prev = prevCountsRef.current;
    prevCountsRef.current = filteredCounts;
    if (!prev || loading || Date.now() - filterChangedAtRef.current < 3000) return;
    const grown = TABS.filter(s => s !== tab && (filteredCounts[s] || 0) > (prev[s] || 0));
    if (grown.length === 0) return;
    setBumpedTabs(cur => new Set([...cur, ...grown]));
    // not cleared on the next count change — that would leave the tab pulsing for good
    setTimeout(() => {
      setBumpedTabs(cur => {
        const next = new Set(cur);
        grown.forEach(s => next.delete(s));
        return next;
      });
    }, 3600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCounts]);
  // opening the tab counts as noticing it
  useEffect(() => {
    setBumpedTabs(cur => {
      if (!cur.has(tab)) return cur;
      const next = new Set(cur);
      next.delete(tab);
      return next;
    });
  }, [tab]);

  // displayed rows
  const displayedRows = useMemo(() => {
    const filtered = applyFilters(data[tab] || []);
    return [...filtered].sort((a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0));
  }, [data, tab, filterText, dateRange, branch, salesperson, department]);

  const rowsById = useMemo(
    () => Object.fromEntries(displayedRows.map(r => [r.cart_guid || r.doc_no, r])),
    [displayedRows]
  );

  // Jump to the highlighted row (from a notification click): right page, scrolled into view.
  // If filters hide it, clear them once. Runs once per highlight so WS reloads don't yank the view.
  const gridApiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const revealedHighlightRef = useRef(null);
  const clearedFiltersForRef = useRef(null);
  useEffect(() => {
    if (!urlHighlightGuid) {
      revealedHighlightRef.current = null;
      clearedFiltersForRef.current = null;
      return;
    }
    if (loading || refreshing || revealedHighlightRef.current === urlHighlightGuid) return;
    const matches = r => (r.cart_guid || r.doc_no) === urlHighlightGuid || r.doc_no === urlHighlightGuid;
    const idx = displayedRows.findIndex(matches);
    if (idx === -1) {
      if (clearedFiltersForRef.current !== urlHighlightGuid && (data[tab] || []).some(matches)) {
        clearedFiltersForRef.current = urlHighlightGuid;
        setFilterText('');
        setBranch('');
        setSalesperson('');
        setDepartment('');
        setDateRange([null, null]);
      }
      return;
    }
    revealedHighlightRef.current = urlHighlightGuid;
    const { pageSize } = paginationModel;
    setPaginationModel(prev => ({ ...prev, page: Math.floor(idx / pageSize) }));
    // Wait for the grid to render the new page before scrolling
    setTimeout(() => {
      gridApiRef.current?.scrollToIndexes?.({ rowIndex: idx % pageSize });
    }, 100);
  }, [urlHighlightGuid, displayedRows, loading, refreshing, data, tab, paginationModel, gridApiRef]);

  const handlePinToggle = useCallback(async (row) => {
    try {
      if (row.status === 'DRAFT') {
        const { data: result } = await api.post(`/api/draft/${row.cart_guid}/pin`);
        setData(prev => ({
          ...prev,
          DRAFT: prev.DRAFT.map(d =>
            d.cart_guid === row.cart_guid ? { ...d, is_pinned: result.is_pinned } : d
          ),
        }));
      } else {
        const { data: result } = await api.post(`/api/orders/${encodeURIComponent(row.doc_no)}/pin`);
        setData(prev => ({
          ...prev,
          [row.status]: prev[row.status].map(r =>
            r.doc_no === row.doc_no ? { ...r, is_pinned: result.is_pinned } : r
          ),
        }));
      }
    } catch (err) {
      console.error('pin toggle error:', err);
    }
  }, []);

  // ――― ฟังก์ชันจัดการเมื่อคลิกปุ่ม Edit ―――
  const handleEdit = async (docNoOrGuid, isDraft = false) => {
    if (editLoading) return;
    setEditLoading(true);
    let acquiredLock = null;
    try {
      // ถ้ากำลังแก้ bill อื่นอยู่ → ถามก่อน
      const currentLock = lockInfoRef.current;
      if (currentLock) {
        const confirmed = await Swal.fire({
          title: 'กำลังแก้ไขบิลอยู่',
          text: `คุณกำลังแก้ไข ${currentLock.doc_no} อยู่ การเปลี่ยนไปแก้บิลใหม่จะไม่บันทึกการแก้ไขที่ค้างอยู่ ต้องการดำเนินการต่อไหม?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d32f2f',
          cancelButtonColor: '#757575',
          confirmButtonText: 'ดำเนินการต่อ',
          cancelButtonText: 'ยกเลิก',
        });
        if (!confirmed.isConfirmed) return;
        try {
          await api.post('/api/unlockBill', { doc_no: currentLock.doc_no, guid: currentLock.guid });
        } catch (err) {
          console.error('unlockBill error:', err);
        }
        setLockInfo(null);
        resetCartState();
      }

      // Guard: warn if current cart has data and we haven't already prompted via lockInfo
      if (!currentLock && (cart.length > 0 || customerId !== null)) {
        const cartDesc = currentOrderNo?.startsWith('DRAFT-')
          ? `Draft ${currentOrderNo.replace('DRAFT-', '')} ที่กำลังแก้ไขอยู่`
          : 'ตะกร้าปัจจุบัน';
        const confirmed = await Swal.fire({
          title: 'มีข้อมูลค้างอยู่',
          text: `การเปิดบิลนี้จะลบ${cartDesc} ต้องการดำเนินการต่อหรือไม่?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d32f2f',
          cancelButtonColor: '#757575',
          confirmButtonText: 'ดำเนินการต่อ',
          cancelButtonText: 'ยกเลิก',
        });
        if (!confirmed.isConfirmed) return;
      }

      if (isDraft) {
        // setCartGuidActive จัดการลบ active cart เดิม + activate draft ในครั้งเดียว (atomic)
        const newGuid = await setCartGuidActive(docNoOrGuid);
        await fetchCartHeader(newGuid);
        navigate('/checkout');
        return;
      }

      // บิลปกติ (QT): lock ก่อนแก้ไข
      const lockRes = await api.post('/api/lockBill', { doc_no: docNoOrGuid });
      acquiredLock = { doc_no: docNoOrGuid, guid: lockRes.data.guid };
      setLockInfo(acquiredLock);

      await deleteCart();
      await loadOrderIntoCart(docNoOrGuid);
      navigate('/checkout');
    } catch (err) {
      console.error(err);
      // โหลดบิลไม่สำเร็จ → ปลดล็อคบิลที่เพิ่งล็อคไว้ ไม่งั้นบิลจะค้างสถานะกำลังแก้ไข
      if (acquiredLock) {
        api.post('/api/unlockBill', acquiredLock).catch(e => console.error('unlockBill error:', e));
        setLockInfo(null);
      }
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: err.response?.data?.error || err.message || 'ไม่สามารถโหลดบิลได้',
      });
    } finally {
      setEditLoading(false);
    }
  };



  const columns = useMemo(() => {
    const renderActions = (row) => {
        if (row.locked_by && row.locked_by !== currentUser?.user_code) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Chip
                size="small"
                icon={<LockIcon fontSize="small" />}
                label={row.last_editor_name || row.locked_by}
                color="warning"
                variant="outlined"
              />
            </Box>
          );
        }
        if (row.guid_code) {
          const editingBy = row.guid_code.substring(26);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Chip
                size="small"
                icon={<LockIcon fontSize="small" />}
                label={editingBy}
                color="warning"
                variant="outlined"
              />
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
            {(row.status === 'QT' || row.status === 'DRAFT') && (
              <IconButton
                size="small"
                disabled={editLoading}
                onClick={() => {
                  if (row.status === 'DRAFT' && row.locked_by === currentUser?.user_code) {
                    navigate('/checkout');
                  } else {
                    handleEdit(
                      row.status === 'DRAFT' ? row.cart_guid : row.doc_no,
                      row.status === 'DRAFT'
                    );
                  }
                }}
              >
                <EditIcon />
              </IconButton>
            )}
            {row.status === 'QT' && (
              <IconButton
                size="small"
                onClick={(e) => { preloadQuotationImage(); setExportMenu({ anchorEl: e.currentTarget, docNo: row.doc_no }); }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            )}
          </Box>
        );
    };
    const actionCol = {
      field: 'actions',
      headerName: 'Action',
      sortable: false,
      flex: 1.5,
      renderCell: ({ row }) => renderActions(row),
    };

    const PRIORITY_CONFIG = {
      urgent:  { emoji: '🔴', label: 'ด่วนมาก',  color: '#b71c1c', bg: '#ffebee' },
      high:    { emoji: '🟠', label: 'ด่วน',    color: '#e65100', bg: '#fff3e0' },
      today:   { emoji: '🟡', label: 'วันนี้',   color: '#f57f17', bg: '#fffde7' },
      waiting: { emoji: '🔵', label: 'รอ', color: '#1565c0', bg: '#e3f2fd' },
    };
    const PRIORITY_ORDER = { urgent: 0, high: 1, today: 2, waiting: 3 };

    if (isCompact) {
      const meta = TAB_META[tab];
      const isDraft = tab === 'DRAFT';
      return [{
        field: 'card',
        headerName: 'แตะบิลเพื่อดูรายละเอียด',
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const p = isDraft ? (PRIORITY_CONFIG[row.draft_priority] || PRIORITY_CONFIG.waiting) : null;
          const badge = tab === 'SO' ? soBadges?.[row.doc_no] : null;
          const note = isDraft ? (row.draft_note || row.remark) : row.remark;
          const refs = tab === 'SO' || tab === 'SL' ? (row.ref_docs || []).filter(Boolean) : [];
          const sale = cleanSaleName(row.sale_name);
          return (
            <Box sx={{ width: '100%', minWidth: 0, py: 1.25, pr: 0.5, display: 'flex', flexDirection: 'column', gap: 0.4, lineHeight: 1.35 }}>
              {/* บรรทัดบน: เลขบิล + สถานะ | ยอดเงิน */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: meta.color, whiteSpace: 'nowrap' }}>
                    {row.doc_no || '-'}
                  </Typography>
                  {row.is_ccs && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.light', flexShrink: 0 }} />}
                  {p && (
                    <Chip size="small" label={`${p.emoji} ${p.label}`}
                      sx={{ fontSize: 11, height: 20, bgcolor: p.bg, color: p.color, border: `1px solid ${p.color}40`, fontWeight: 600 }} />
                  )}
                  {badge && (
                    <Chip size="small" variant="outlined" color={badge.tone} label={badge.label} sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                  ฿{fmt2(row.total_amount || 0)}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{
                fontWeight: 500, overflow: 'hidden', wordBreak: 'break-word',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}>
                {row.customer_name || '-'}
              </Typography>

              {note && (
                <Typography variant="caption" color="text.secondary" noWrap>📝 {note}</Typography>
              )}
              {refs.length > 0 && (
                <Typography variant="caption" color="text.secondary" noWrap>อ้างอิง: {refs.join(', ')}</Typography>
              )}

              {/* บรรทัดล่าง: วันที่ · เซลล์ | ปุ่ม */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 30 }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {[
                    row.doc_date ? dayjs(row.doc_date).format('DD/MM/BBBB') : null,
                    sale || null,
                    isDraft && row.updated_at ? `แก้ไข ${friendlyTime(row.updated_at)}` : null,
                  ].filter(Boolean).join(' · ')}
                </Typography>
                {/* กดปุ่มในการ์ดไม่ให้เปิด drawer */}
                <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => handlePinToggle(row)}
                    sx={{ color: row.is_pinned ? '#f57c00' : 'action.disabled' }}
                  >
                    {row.is_pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                  </IconButton>
                  {renderActions(row)}
                </Box>
              </Box>
            </Box>
          );
        },
      }];
    }

    if (tab === 'DRAFT') {
      return [
        {
          field: 'is_pinned',
          headerName: '',
          width: 44,
          sortable: false,
          disableColumnMenu: true,
          renderCell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Tooltip title={row.is_pinned ? 'เลิก Pin' : 'Pin draft นี้'}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handlePinToggle(row); }}
                  sx={{ color: row.is_pinned ? '#f57c00' : 'action.disabled' }}
                >
                  {row.is_pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          ),
        },
        {
          field: 'draft_priority',
          headerName: 'Priority',
          flex: 1,
          sortComparator: (a, b) => (PRIORITY_ORDER[a] ?? 99) - (PRIORITY_ORDER[b] ?? 99),
          renderCell: ({ value }) => {
            const p = PRIORITY_CONFIG[value] || PRIORITY_CONFIG.waiting;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Chip
                  size="small"
                  label={`${p.emoji} ${p.label}`}
                  sx={{ fontSize: 11, height: 22, bgcolor: p.bg, color: p.color, border: `1px solid ${p.color}40`, fontWeight: 600, px: 0.25 }}
                />
              </Box>
            );
          },
        },
        {
          field: 'doc_date',
          headerName: 'วันที่เอกสาร',
          flex: 1,
          renderCell: ({ value }) => value ? dayjs(value).format('DD/MM/BBBB') : '',
        },
        { field: 'doc_no', headerName: 'Draft No.', flex: 1 },
        { field: 'customer_name', headerName: 'ลูกค้า', flex: 1.5 },
        {
          field: 'total_amount',
          headerName: 'จำนวนเงิน',
          flex: 1,
          renderCell: ({ value }) =>
            typeof value === 'number'
              ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
              : '',
        },
        {
          field: 'sale_name',
          headerName: 'พนักงานขาย',
          flex: 1,
          renderCell: ({ value }) => {
            const name = cleanSaleName(value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', overflow: 'hidden' }}>
                <Typography variant="body2" noWrap>{name || '-'}</Typography>
              </Box>
            );
          },
        },
        {
          field: 'remark',
          headerName: 'หมายเหตุบิล',
          flex: 1.5,
          renderCell: ({ value }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', overflow: 'hidden' }}>
              <Typography variant="body2" noWrap sx={{ lineHeight: 1.3 }}>
                {value || '-'}
              </Typography>
            </Box>
          ),
        },
        {
          field: 'creator_name',
          headerName: 'ผู้สร้าง',
          flex: 1.5,
          renderCell: ({ row }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 0.25, overflow: 'hidden' }}>
              <Typography variant="body2" noWrap sx={{ lineHeight: 1.3 }}>{row.creator_name || '-'}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.3 }}>
                {friendlyTime(row.created_at)}
              </Typography>
            </Box>
          ),
        },
        {
          field: 'last_editor_name',
          headerName: 'แก้ไขล่าสุด',
          flex: 1.5,
          renderCell: ({ row }) => {
            if (!row.updated_at) return <Box sx={{ height: '100%' }} />;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 0.25, overflow: 'hidden' }}>
                <Typography variant="body2" noWrap sx={{ lineHeight: 1.3 }}>{row.last_editor_name || '-'}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.3 }}>
                  {friendlyTime(row.updated_at)}
                </Typography>
              </Box>
            );
          },
        },
        {
          field: 'draft_note',
          headerName: 'หมายเหตุ Draft',
          flex: 1.5,
          renderCell: ({ value }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', overflow: 'hidden' }}>
              <Typography variant="body2" noWrap sx={{ lineHeight: 1.3, color: value ? 'text.primary' : 'text.disabled' }}>
                {value || '-'}
              </Typography>
            </Box>
          ),
        },
        actionCol,
      ];
    }

    const baseCols = [
      {
        field: 'is_pinned',
        headerName: '',
        width: 44,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Tooltip title={row.is_pinned ? 'เลิก Pin' : 'Pin บิลนี้'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handlePinToggle(row); }}
                sx={{ color: row.is_pinned ? '#f57c00' : 'action.disabled' }}
              >
                {row.is_pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: "doc_date",
        headerName: "วันที่เอกสาร",
        flex: 1,
        renderCell: ({ value }) =>
          value ? dayjs(value).format("DD/MM/BBBB") : "",
      },
      {
        field: 'doc_no',
        headerName: 'เลขที่เอกสาร',
        flex: 1,
        renderCell: ({ row, value }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {value}
            {row.is_ccs && (
              <Tooltip title="สร้างจาก CCS ERP">
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.light', flexShrink: 0 }} />
              </Tooltip>
            )}
            {tab === 'SO' && soBadges?.[value] && (
              <Tooltip title={soBadges[value].short ? `ยังไม่พอ: ${soBadges[value].short.join(', ')} (เทียบคงเหลือรวมทุกคลัง)` : 'คงเหลือรวมทุกคลังพอสำหรับทุกรายการที่ยังไม่ออกบิล'}>
                <Chip
                  size="small" variant="outlined" color={soBadges[value].tone}
                  label={soBadges[value].label} sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}
          </Box>
        ),
      },
      { field: 'customer_name', headerName: 'ลูกค้า',      flex: 2 },
      {
        field: 'total_amount',
        headerName: 'จำนวนเงิน',
        flex: 1,
        renderCell: ({ value }) =>
          typeof value === 'number'
            ? new Intl.NumberFormat('en-US',{ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
            : ''
      },
      { field: 'remark',    headerName: 'หมายเหตุ', flex: 2, hide: isMobile },
      { field: 'sale_name', headerName: 'เซลล์',    flex: 1 },
      { field: 'created_by',headerName: 'ผู้สร้าง',  flex: 1 },
      { field: 'updated_by',headerName: 'ผู้แก้ไข',  flex: 1 },
    ];

    if (tab !== 'QT') {
      baseCols.push({
        field: 'ref_docs',
        headerName: 'อ้างอิงจาก',
        flex: 2,
        renderCell: ({ row }) =>
          Array.isArray(row.ref_docs) ? row.ref_docs.join(', ') : ''
      });
    }

    baseCols.push(actionCol);
    return baseCols;
  }, [tab, isMobile, isCompact, soBadges, editLoading, currentUser?.user_code]);


  return (
    // สูงพอดีจอ (100vh - แถบด้านบน) — ตารางกินพื้นที่ที่เหลือเอง ไม่ต้องคำนวณ px ตายตัว จึงไม่เกิด scrollbar ทั้งหน้า
    <Box sx={{ width: '100%', pt: 2, boxSizing: 'border-box', height: 'calc(100vh - var(--nav-h))', '@supports (height: 100dvh)': { height: 'calc(100dvh - var(--nav-h))' }, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', p: 2, flexShrink: 0 }}>
        <TextField
          label="ค้นหา"
          size="small"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          sx={{ flex: 2, minWidth: 160 }}
        />

        <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
          <InputLabel>สาขา</InputLabel>
          <Select value={branch} onChange={e => setBranch(e.target.value)} label="สาขา">
            <MenuItem value=""><em>ทั้งหมด</em></MenuItem>
            {branchList.map(b => (
              <MenuItem key={b.code} value={b.code}>{b.name_1}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
          <InputLabel>แผนก</InputLabel>
          <Select value={department} onChange={e => setDepartment(e.target.value)} label="แผนก">
            <MenuItem value=""><em>ทั้งหมด</em></MenuItem>
            {departmentList.map(d => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
          <InputLabel>พนักงานขาย</InputLabel>
          <Select value={salesperson} onChange={e => setSalesperson(e.target.value)} label="พนักงานขาย">
            <MenuItem value=""><em>ทั้งหมด</em></MenuItem>
            {salespersons.map(u => (
              <MenuItem key={u.code} value={u.code}>{u.name_1}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={yearAdapter} adapterLocale="th">
          <Box sx={{ display: 'flex', gap: 1, flex: 2, minWidth: 220 }}>
            <DatePicker
              label="จากวันที่"
              value={dateRange[0]}
              onChange={(d) => setDateRange([d, dateRange[1]])}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label="ถึงวันที่"
              value={dateRange[1]}
              onChange={(d) => setDateRange([dateRange[0], d])}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Box>
        </LocalizationProvider>

        {(filterText || branch || salesperson || department || dateRange[0] || dateRange[1]) && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => {
              setFilterText('');
              setBranch('');
              setSalesperson('');
              setDepartment('');
              setDateRange([null, null]);
            }}
          >
            ล้างตัวกรอง
          </Button>
        )}

        {/* <IconButton onClick={() => window.location.reload()}>
          <RefreshIcon />
        </IconButton> */}
        {/* <Button startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print
        </Button> */}
      </Box>

      <Tabs
        variant="fullWidth"
        value={tab}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          minHeight: { xs: 56, sm: 48 },
          '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: TAB_META[tab]?.color },
        }}
        onChange={(e, newValue) => {
          setTab(newValue);
          setRowSelectionModel([]);
          setPaginationModel(prev => ({ ...prev, page: 0 }));
          const next = {};
          for (const key of ["branch", "sales", "dept", "from", "to"]) {
            const val = searchParams.get(key);
            if (val) next[key] = val;
          }
          // ใส่ tab ลงไป
          next.tab = newValue;
          setSearchParams(next, { replace: true });
        }}
      >
        {TABS.map((code) => {
          const meta = TAB_META[code];
          const Icon = meta.icon;
          const active = tab === code;
          const count = filteredCounts[code] || 0;
          const capped = code !== 'DRAFT' && count >= ORDER_LIMIT_PER_STATUS;
          return (
            <Tab
              key={code}
              value={code}
              sx={{
                minHeight: { xs: 56, sm: 48 },
                minWidth: 0,
                px: { xs: 0.5, sm: 2 },
                textTransform: 'none',
                color: 'text.secondary',
                transition: 'background-color .15s, color .15s',
                '&:hover': { bgcolor: meta.bg, color: meta.color },
                '&.Mui-selected': { color: meta.color, bgcolor: meta.bg, fontWeight: 700 },
              }}
              label={
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 0.25, sm: 1 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Icon sx={{ fontSize: 20, display: { xs: 'none', sm: 'inline-flex' } }} />
                    <Box component="span" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.92rem' } }}>
                      {TAB_LABELS[code]}
                    </Box>
                  </Box>
                  <Box component="span" sx={{
                    minWidth: 24, px: 0.875, borderRadius: 10, textAlign: 'center',
                    fontSize: '0.75rem', fontWeight: 700, lineHeight: '20px',
                    // any count > 0 gets the tab's solid color so a lone "1" stands out; 0 fades away
                    bgcolor: active || count > 0 ? meta.color : 'transparent',
                    color: active || count > 0 ? '#fff' : 'text.disabled',
                    border: '1px solid',
                    borderColor: active || count > 0 ? meta.color : 'grey.300',
                    opacity: !active && count > 0 ? 0.85 : 1,
                    transition: 'background-color .15s, color .15s',
                    ...(bumpedTabs.has(code) && {
                      opacity: 1,
                      animation: 'tabCountBump 0.9s ease-in-out 4',
                      '@keyframes tabCountBump': {
                        '0%, 100%': { transform: 'scale(1)', boxShadow: `0 0 0 0 ${meta.color}66` },
                        '50%': { transform: 'scale(1.18)', boxShadow: `0 0 0 5px ${meta.color}00` },
                      },
                    }),
                  }}>
                    {count.toLocaleString('en-US')}{capped ? '+' : ''}
                  </Box>
                </Box>
              }
            />
          );
        })}
      </Tabs>

      {searchTrailDoc && (
        <Box sx={{
          mb: 1, px: 2, py: 1.5, borderRadius: 2, flexShrink: 0,
          border: '1px solid #90caf9', bgcolor: '#f5f9ff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
              <b>{searchTrailDoc.doc_no}</b>{' '}
              {Number(searchTrailDoc.used_status) === 1 && searchTrailDoc.status !== 'SL'
                ? 'ถูกดึงไปใช้เปิดบิลต่อแล้ว จึงไม่แสดงในตาราง'
                : 'ไม่อยู่ในตารางตามตัวกรองปัจจุบัน'}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
                คลิกบิลเพื่อดูรายละเอียด
              </Typography>
            </Typography>
            <DocCompareButton rows={searchTrail} />
          </Box>
          <DocTrail
            rows={searchTrail}
            currentDocNo={searchTrailDoc.doc_no}
            onOpen={handleRefDocOpen}
            orientation={isMobile ? 'compact' : 'horizontal'}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, width: '100%', position: 'relative' }}>
        {refreshing && !loading && (
          // sits on the bottom edge of the column headers (default 56px), clear of the tabs above
          <LinearProgress sx={{ position: 'absolute', top: isCompact ? 40 : 56, left: 1, right: 1, height: 4, zIndex: 5 }} />
        )}
        <DataGrid
          apiRef={gridApiRef}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rows={displayedRows}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableSelectionOnClick
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          pageSizeOptions={[20, 50, 100]}
          slots={{ noRowsOverlay: EmptyState, footer: TableFooter }}
          slotProps={{ footer: { selectionModel: rowSelectionModel, rowsById }, noRowsOverlay: { searching: refreshing } }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                created_by: false,
                updated_by: false,
              }
            }
          }}
          onRowDoubleClick={(params) => setDrawerOrder(params.row)}
          // การ์ดบนมือถือ/แท็บเล็ต: แตะครั้งเดียวเปิดรายละเอียด (ยกเว้นช่อง checkbox)
          onCellClick={isCompact ? (params) => { if (params.field !== '__check__') setDrawerOrder(params.row); } : undefined}
          disableRowSelectionOnClick={isCompact}
          getRowHeight={isCompact ? () => 'auto' : undefined}
          columnHeaderHeight={isCompact ? 40 : 56}
          getRowId={(row) => row.cart_guid || row.doc_no}
          getRowClassName={(params) => {
            const guid = params.row.cart_guid || params.row.doc_no;
            if (urlHighlightGuid && (guid === urlHighlightGuid || params.row.doc_no === urlHighlightGuid)) return 'row-highlighted';
            if (params.row.locked_by || params.row.guid_code) return 'row-locked';
            if (newDraftGuids.has(params.row.cart_guid)) return 'row-new';
            if (params.row.is_pinned) return 'row-pinned';
            return '';
          }}
          sx={{
            ...(isCompact && {
              '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', cursor: 'pointer' },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.75rem', color: 'text.secondary' },
            }),
            '& .row-locked': {
              bgcolor: 'rgba(255, 152, 0, 0.08)',
              '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.14)' },
            },
            '& .row-new': {
              bgcolor: 'rgba(46, 125, 50, 0.10)',
              '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.16)' },
            },
            '@keyframes rowHighlightPulse': {
              '0%, 100%': { backgroundColor: 'rgba(25, 118, 210, 0.18)' },
              '50%':      { backgroundColor: 'rgba(25, 118, 210, 0.40)' },
            },
            '& .row-highlighted': {
              bgcolor: 'rgba(25, 118, 210, 0.18)',
              boxShadow: 'inset 4px 0 0 #1976d2',
              fontWeight: 600,
              animation: 'rowHighlightPulse 0.8s ease-in-out 3',
              '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.24)' },
            },
            '& .row-pinned': {
              bgcolor: 'rgba(245, 124, 0, 0.06)',
              '&:hover': { bgcolor: 'rgba(245, 124, 0, 0.12)' },
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={exportMenu?.anchorEl}
        open={Boolean(exportMenu)}
        onClose={() => setExportMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            // previewPDF เปิดหน้าต่างก่อน await แรก จึงต้องเรียกตรงจากการกดเมนูนี้
            const docNo = exportMenu.docNo;
            setExportMenu(null);
            previewPDF(docNo, QuotationDocument);
          }}
        >
          <ListItemIcon><PictureAsPdfIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
          <ListItemText primary="ไฟล์ PDF" secondary="ดาวน์โหลด / พิมพ์" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const docNo = exportMenu.docNo;
            setExportMenu(null);
            shareQuotationImage(docNo, QuotationDocument);
          }}
        >
          <ListItemIcon><ImageOutlinedIcon fontSize="small" sx={{ color: 'success.main' }} /></ListItemIcon>
          <ListItemText primary="รูปภาพ" secondary="ดาวน์โหลด / แชร์ส่ง LINE" />
        </MenuItem>
      </Menu>

      <Drawer anchor="right" open={Boolean(drawerOrder)} onClose={() => setDrawerOrder(null)}>
        {drawerOrder && (
          <Box sx={{
            width: isMobile ? '100vw' : (['DELETED', 'QT', 'SO', 'SL', 'DRAFT'].includes(drawerOrder.status) ? 480 : 360),
            display: 'flex', flexDirection: 'column', height: '100%',
          }}>
            {/* Header */}
            <Box sx={{
              px: 2.5, py: 1.75,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
            }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {drawerOrder.doc_no || '—'}
                  </Typography>
                  {drawerOrder.doc_no && <CopyDocNoButton docNo={drawerOrder.doc_no} size={16} />}
                </Box>
                {drawerOrder.status === 'DELETED' && (
                  <Chip size="small" label="🗑️ ถูกลบแล้ว"
                    sx={{ mt: 0.5, bgcolor: '#fbe9e7', color: '#b71c1c', border: '1px solid #b71c1c30', fontWeight: 600 }}
                  />
                )}
                {drawerOrder.status === 'QT' && (
                  <Chip size="small" label="ใบเสนอราคา"
                    sx={{ mt: 0.5, bgcolor: '#e3f2fd', color: '#1565c0', border: '1px solid #1565c030', fontWeight: 600 }}
                  />
                )}
                {drawerOrder.status === 'SO' && (
                  <Chip size="small" label="ใบสั่งขาย"
                    sx={{ mt: 0.5, bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d3230', fontWeight: 600 }}
                  />
                )}
                {drawerOrder.status === 'SL' && (
                  <Chip size="small" label="ใบกำกับภาษี"
                    sx={{ mt: 0.5, bgcolor: '#fce4ec', color: '#c62828', border: '1px solid #c6282830', fontWeight: 600 }}
                  />
                )}
                {drawerOrder.status === 'DRAFT' && (
                  <Chip size="small" label="Draft"
                    sx={{ mt: 0.5, bgcolor: '#f3e5f5', color: '#6a1b9a', border: '1px solid #6a1b9a30', fontWeight: 600 }}
                  />
                )}
              </Box>
              <IconButton size="small" onClick={() => setDrawerOrder(null)} sx={{ mt: 0.25 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
              {drawerOrder.status === 'DELETED' ? (
                <>
                  {/* Deletion info */}
                  <Box sx={{ bgcolor: '#fbe9e7', borderRadius: 1.5, p: 1.5, mb: 2.5, border: '1px solid #ffcdd2' }}>
                    <Typography variant="caption" sx={{ color: '#b71c1c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      ข้อมูลการลบ
                    </Typography>
                    <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', alignItems: 'baseline' }}>
                      <Typography variant="body2" color="text.secondary">ลบโดย</Typography>
                      <Typography variant="body2" fontWeight={600}>{drawerOrder.deleted_by || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">วันที่ลบ</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {drawerOrder.deleted_at ? dayjs(drawerOrder.deleted_at).format('DD/MM/BBBB HH:mm') : '—'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Draft info */}
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    ข้อมูล Draft
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2.5, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'baseline' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ลูกค้า</Typography>
                    <Typography variant="body2" fontWeight={500}>{drawerOrder.customer_name || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>พนักงานขาย</Typography>
                    <Typography variant="body2">{drawerOrder.sale_name || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>วันที่เอกสาร</Typography>
                    <Typography variant="body2">{drawerOrder.doc_date ? dayjs(drawerOrder.doc_date).format('DD/MM/BBBB') : '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>จำนวนเงิน</Typography>
                    <Typography variant="body2" fontWeight={700} color="success.dark">
                      {fmt2(parseFloat(drawerOrder.total_sum_amount || 0))} บาท
                    </Typography>

                    {parseFloat(drawerOrder.total_weight || 0) > 0 && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>น้ำหนักรวม</Typography>
                      <Typography variant="body2">{fmt2(parseFloat(drawerOrder.total_weight || 0))} กก.</Typography>
                    </>}

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ผู้สร้าง</Typography>
                    <Typography variant="body2">{drawerOrder.creator_name || drawerOrder.creator_code || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>สร้างเมื่อ</Typography>
                    <Typography variant="body2">
                      {drawerOrder.create_datetime ? dayjs(drawerOrder.create_datetime).format('DD/MM/BBBB HH:mm') : '—'}
                    </Typography>

                    {drawerOrder.remark && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>หมายเหตุบิล</Typography>
                      <Typography variant="body2">{drawerOrder.remark}</Typography>
                    </>}

                    {drawerOrder.draft_note && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>หมายเหตุ Draft</Typography>
                      <Typography variant="body2">{drawerOrder.draft_note}</Typography>
                    </>}

                    {drawerOrder.draft_priority && DRAFT_PRIORITY_CONFIG[drawerOrder.draft_priority] && (() => {
                      const p = DRAFT_PRIORITY_CONFIG[drawerOrder.draft_priority];
                      return (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ความสำคัญ</Typography>
                          <Chip size="small" label={`${p.emoji} ${p.label}`}
                            sx={{ fontSize: 11, height: 20, bgcolor: p.bg, color: p.color, border: `1px solid ${p.color}40`, fontWeight: 600, px: 0.25, justifySelf: 'start' }}
                          />
                        </>
                      );
                    })()}
                  </Box>

                  {/* Item list */}
                  {Array.isArray(drawerOrder.details) && drawerOrder.details.length > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          รายการสินค้า
                        </Typography>
                        <Chip size="small" label={`${drawerOrder.details.length} รายการ`}
                          sx={{ height: 18, fontSize: 11, bgcolor: 'grey.100' }}
                        />
                      </Box>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', mb: 2 }}>
                        {drawerOrder.details.map((d, i) => (
                          <Box key={i} sx={{
                            px: 1.5, py: 1,
                            bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50',
                            borderBottom: i < drawerOrder.details.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                          }}>
                            <Typography variant="body2" sx={{ lineHeight: 1.4, mb: 0.5, wordBreak: 'break-word' }}>
                              {d.item_name}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {fmt2(parseFloat(d.qty || 0))} {d.unit_code} × {fmt2(parseFloat(d.price || 0))} บาท
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="success.dark">
                                {fmt2(parseFloat(d.sum_amount || 0))} บาท
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">รวม {drawerOrder.details.length} รายการ</Typography>
                        <Typography variant="body1" fontWeight={700} color="success.dark">
                          {fmt2(parseFloat(drawerOrder.total_sum_amount || 0))} บาท
                        </Typography>
                      </Box>
                    </>
                  )}
                </>
              ) : ['QT', 'SO', 'SL'].includes(drawerOrder.status) ? (
                <>
                  {/* Bill info grid */}
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    ข้อมูลบิล
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2.5, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'baseline' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ลูกค้า</Typography>
                    <Typography variant="body2" fontWeight={500}>{drawerOrder.customer_name || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>พนักงานขาย</Typography>
                    <Typography variant="body2">{drawerOrder.sale_name || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>วันที่เอกสาร</Typography>
                    <Typography variant="body2">{drawerOrder.doc_date ? dayjs(drawerOrder.doc_date).format('DD/MM/BBBB') : '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>จำนวนเงิน</Typography>
                    <Typography variant="body2" fontWeight={700} color="success.dark">
                      {fmt2(parseFloat(drawerOrder.total_amount || 0))} บาท
                    </Typography>

                    {parseFloat(drawerOrder.total_weight || 0) > 0 && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>น้ำหนักรวม</Typography>
                      <Typography variant="body2">{fmt2(parseFloat(drawerOrder.total_weight || 0))} กก.</Typography>
                    </>}

                    {drawerOrder.created_by && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ผู้สร้าง</Typography>
                      <Typography variant="body2">{drawerOrder.created_by}</Typography>
                    </>}

                    {billHeader?.doc_no === drawerOrder.doc_no && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>สถานที่ส่ง</Typography>
                      <DeliveryPlace header={billHeader} />
                    </>}

                    {drawerOrder.remark && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>หมายเหตุ</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{drawerOrder.remark}</Typography>
                    </>}

                  </Box>

                  {/* Doc trail QT → SO → SL */}
                  {drawerTrail && drawerTrail.length > 1 && (
                    <Box sx={{ mb: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          เส้นทางเอกสาร
                        </Typography>
                        <DocCompareButton rows={drawerTrail} />
                      </Box>
                      <Box>
                        <DocTrail rows={drawerTrail} currentDocNo={drawerOrder.doc_no} disableCurrent onOpen={handleRefDocOpen} orientation="compact" />
                      </Box>
                    </Box>
                  )}

                  {/* Item list */}
                  {billDetailsLoading ? (
                    <Typography variant="body2" color="text.secondary">กำลังโหลดรายการสินค้า...</Typography>
                  ) : billDetails && billDetails.length > 0 ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          รายการสินค้า
                        </Typography>
                        <Chip size="small" label={`${billDetails.length} รายการ`}
                          sx={{ height: 18, fontSize: 11, bgcolor: 'grey.100' }}
                        />
                      </Box>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', mb: 2 }}>
                        {billDetails.map((d, i) => (
                          <Box key={i} sx={{
                            px: 1.5, py: 1,
                            bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50',
                            borderBottom: i < billDetails.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                          }}>
                            <Typography variant="body2" sx={{ lineHeight: 1.4, mb: 0.5, wordBreak: 'break-word' }}>
                              {d.item_name}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {fmt2(parseFloat(d.qty || 0))} {d.unit_code} × {fmt2(parseFloat(d.price || 0))} บาท
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="success.dark">
                                {fmt2(parseFloat(d.qty * d.price || 0))} บาท
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">รวม {billDetails.length} รายการ</Typography>
                        <Typography variant="body1" fontWeight={700} color="success.dark">
                          {fmt2(parseFloat(drawerOrder.total_amount || 0))} บาท
                        </Typography>
                      </Box>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  {/* Draft info grid */}
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    ข้อมูล Draft
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2.5, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'baseline' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ลูกค้า</Typography>
                    <Typography variant="body2" fontWeight={500}>{drawerOrder.customer_name || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>พนักงานขาย</Typography>
                    <Typography variant="body2">{drawerOrder.sale_name || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>วันที่เอกสาร</Typography>
                    <Typography variant="body2">{drawerOrder.doc_date ? dayjs(drawerOrder.doc_date).format('DD/MM/BBBB') : '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>จำนวนเงิน</Typography>
                    <Typography variant="body2" fontWeight={700} color="success.dark">
                      {fmt2(parseFloat(drawerOrder.total_amount || 0))} บาท
                    </Typography>

                    {parseFloat(drawerOrder.total_weight || 0) > 0 && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>น้ำหนักรวม</Typography>
                      <Typography variant="body2">{fmt2(parseFloat(drawerOrder.total_weight || 0))} กก.</Typography>
                    </>}

                    {(drawerOrder.creator_name || drawerOrder.created_by) && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ผู้สร้าง</Typography>
                      <Typography variant="body2">{drawerOrder.creator_name || drawerOrder.created_by}</Typography>
                    </>}

                    {drawerOrder.created_at && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>สร้างเมื่อ</Typography>
                      <Typography variant="body2">{dayjs(drawerOrder.created_at).format('DD/MM/BBBB HH:mm')}</Typography>
                    </>}

                    {drawerOrder.draft_note && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>หมายเหตุ Draft</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{drawerOrder.draft_note}</Typography>
                    </>}

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>หมายเหตุบิล</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{drawerOrder.remark || '—'}</Typography>

                    {drawerOrder.locked_by && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>กำลังแก้ไขโดย</Typography>
                      <Typography variant="body2" color="warning.main" fontWeight={600}>{drawerOrder.last_editor_name || drawerOrder.locked_by}</Typography>
                    </>}

                    {drawerOrder.draft_priority && DRAFT_PRIORITY_CONFIG[drawerOrder.draft_priority] && (() => {
                      const p = DRAFT_PRIORITY_CONFIG[drawerOrder.draft_priority];
                      return (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ความสำคัญ</Typography>
                          <Chip size="small" label={`${p.emoji} ${p.label}`}
                            sx={{ fontSize: 11, height: 20, bgcolor: p.bg, color: p.color, border: `1px solid ${p.color}40`, fontWeight: 600, px: 0.25, justifySelf: 'start' }}
                          />
                        </>
                      );
                    })()}
                  </Box>

                  {/* Item list */}
                  {billDetailsLoading ? (
                    <Typography variant="body2" color="text.secondary">กำลังโหลดรายการสินค้า...</Typography>
                  ) : billDetails && billDetails.length > 0 ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          รายการสินค้า
                        </Typography>
                        <Chip size="small" label={`${billDetails.length} รายการ`}
                          sx={{ height: 18, fontSize: 11, bgcolor: 'grey.100' }}
                        />
                      </Box>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', mb: 2 }}>
                        {billDetails.map((d, i) => (
                          <Box key={i} sx={{
                            px: 1.5, py: 1,
                            bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50',
                            borderBottom: i < billDetails.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                          }}>
                            <Typography variant="body2" sx={{ lineHeight: 1.4, mb: 0.5, wordBreak: 'break-word' }}>
                              {d.item_name}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {fmt2(parseFloat(d.qty || 0))} {d.unit_code} × {fmt2(parseFloat(d.price || 0))} บาท
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="success.dark">
                                {fmt2(parseFloat(d.sum_amount || 0))} บาท
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">รวม {billDetails.length} รายการ</Typography>
                        <Typography variant="body1" fontWeight={700} color="success.dark">
                          {fmt2(parseFloat(drawerOrder.total_amount || 0))} บาท
                        </Typography>
                      </Box>
                    </>
                  ) : null}
                </>
              )}
            </Box>

            {/* Footer: restore button for deleted drafts */}
            {drawerOrder.status === 'DELETED' && (
              <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<RestoreFromTrashIcon />}
                  onClick={handleRestoreDraft}
                  disabled={restoreLoading}
                  sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                >
                  {restoreLoading ? 'กำลังกู้คืน...' : 'กู้คืน Draft'}
                </Button>
              </Box>
            )}

            {/* Footer: action buttons for QT */}
            {drawerOrder.status === 'QT' && (
              <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setDrawerOrder(null);
                    handleEdit(drawerOrder.doc_no, false);
                  }}
                >
                  แก้ไข
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<PictureAsPdfIcon />}
                  onClick={(e) => { preloadQuotationImage(); setExportMenu({ anchorEl: e.currentTarget, docNo: drawerOrder.doc_no }); }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  PDF / รูปภาพ
                </Button>
              </Box>
            )}

            {/* Footer: edit button for DRAFT */}
            {drawerOrder.status === 'DRAFT' && (
              <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EditIcon />}
                  disabled={!!drawerOrder.locked_by && drawerOrder.locked_by !== currentUser?.user_code}
                  onClick={() => {
                    setDrawerOrder(null);
                    if (drawerOrder.locked_by === currentUser?.user_code) {
                      navigate('/checkout');
                    } else {
                      handleEdit(drawerOrder.cart_guid, true);
                    }
                  }}
                >
                  {drawerOrder.locked_by && drawerOrder.locked_by !== currentUser?.user_code
                    ? `กำลังแก้ไขโดย ${drawerOrder.last_editor_name || drawerOrder.locked_by}`
                    : 'แก้ไข'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Drawer>

      {/* Secondary drawer: referenced document */}
      <Drawer anchor="right" open={Boolean(refDrawerOrder)} onClose={() => { setRefDrawerOrder(null); setRefBillDetails(null); }}>
        {refDrawerOrder && (
          <Box sx={{ width: isMobile ? '100vw' : 480, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>{refDrawerOrder.doc_no}</Typography>
                  <CopyDocNoButton docNo={refDrawerOrder.doc_no} size={16} />
                </Box>
                {refDrawerOrder.status === 'QT' && <Chip size="small" label="ใบเสนอราคา" sx={{ mt: 0.5, bgcolor: '#e3f2fd', color: '#1565c0', border: '1px solid #1565c030', fontWeight: 600 }} />}
                {refDrawerOrder.status === 'SO' && <Chip size="small" label="ใบสั่งขาย" sx={{ mt: 0.5, bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d3230', fontWeight: 600 }} />}
                {refDrawerOrder.status === 'SL' && <Chip size="small" label="ใบกำกับภาษี" sx={{ mt: 0.5, bgcolor: '#fce4ec', color: '#c62828', border: '1px solid #c6282830', fontWeight: 600 }} />}
              </Box>
              <IconButton size="small" onClick={() => { setRefDrawerOrder(null); setRefBillDetails(null); }} sx={{ mt: 0.25 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
              {refDrawerLoading ? (
                <Typography variant="body2" color="text.secondary">กำลังโหลด...</Typography>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    ข้อมูลบิล
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2.5, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'baseline' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ลูกค้า</Typography>
                    <Typography variant="body2" fontWeight={500}>{refDrawerOrder.customer_name || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>พนักงานขาย</Typography>
                    <Typography variant="body2">{refDrawerOrder.sale_name || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>วันที่เอกสาร</Typography>
                    <Typography variant="body2">{refDrawerOrder.doc_date ? dayjs(refDrawerOrder.doc_date).format('DD/MM/BBBB') : '—'}</Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>จำนวนเงิน</Typography>
                    <Typography variant="body2" fontWeight={700} color="success.dark">
                      {fmt2(parseFloat(refDrawerOrder.total_amount || 0))} บาท
                    </Typography>

                    {parseFloat(refDrawerOrder.total_weight || 0) > 0 && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>น้ำหนักรวม</Typography>
                      <Typography variant="body2">{fmt2(parseFloat(refDrawerOrder.total_weight || 0))} กก.</Typography>
                    </>}

                    {refDrawerOrder.created_by && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>ผู้สร้าง</Typography>
                      <Typography variant="body2">{refDrawerOrder.created_by}</Typography>
                    </>}

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>สถานที่ส่ง</Typography>
                    <DeliveryPlace header={refDrawerOrder} />

                    {refDrawerOrder.remark && <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>หมายเหตุ</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{refDrawerOrder.remark}</Typography>
                    </>}

                  </Box>

                  {/* Doc trail — clicking another step swaps this drawer to that bill */}
                  {refDrawerTrail && refDrawerTrail.length > 1 && (
                    <Box sx={{ mb: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          เส้นทางเอกสาร
                        </Typography>
                        <DocCompareButton rows={refDrawerTrail} />
                      </Box>
                      <Box>
                        <DocTrail rows={refDrawerTrail} currentDocNo={refDrawerOrder.doc_no} disableCurrent onOpen={handleRefDocOpen} orientation="compact" />
                      </Box>
                    </Box>
                  )}

                  {refBillDetails && refBillDetails.length > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          รายการสินค้า
                        </Typography>
                        <Chip size="small" label={`${refBillDetails.length} รายการ`} sx={{ height: 18, fontSize: 11, bgcolor: 'grey.100' }} />
                      </Box>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', mb: 2 }}>
                        {refBillDetails.map((d, i) => (
                          <Box key={i} sx={{ px: 1.5, py: 1, bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50', borderBottom: i < refBillDetails.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                            <Typography variant="body2" sx={{ lineHeight: 1.4, mb: 0.5, wordBreak: 'break-word' }}>{d.item_name}</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {fmt2(parseFloat(d.qty || 0))} {d.unit_code} × {fmt2(parseFloat(d.price || 0))} บาท
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="success.dark">
                                {fmt2(parseFloat(d.qty * d.price || 0))} บาท
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">รวม {refBillDetails.length} รายการ</Typography>
                        <Typography variant="body1" fontWeight={700} color="success.dark">
                          {fmt2(parseFloat(refDrawerOrder.total_amount || 0))} บาท
                        </Typography>
                      </Box>
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
